import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { parse, isValid, format } from "date-fns";
import { darkDatePickerSlotProps } from "../../../../themes/datePickerStyles";
import { SubAssembly } from "../../../../types/subAssembly";
import {
  getSubAssemblyTypesForLandingGear,
  isAttachedSubAssembly,
  LANDING_GEAR_TYPE_OPTIONS,
} from "../../../../utils/subAssemblyUtils";
import {
  filterSupportedAirframeFamilies,
  normalizeAirframeFamily,
  useAirframeFamilies,
} from "../../components/hooks/useSubAssemblyCatalog";
import { useSubAssemblyService } from "../../../../services/subAssemblyService";
import {
  normalizeOffWingTypeForForm,
  OFF_WING_TYPE_OPTIONS,
} from "../../../../utils/offWingType";
import { useShopVisitReasonService } from "../../../../api/shopVisitReasonService";
import { ShopVisitReasonItem } from "../../../../types/shopVisitReason";
import { ValidationErrorsDialog } from "../../../common/validation-errors-dialog";

type Props = {
  open: boolean;
  form: Partial<SubAssembly> & { airframeFamily?: string };
  isCreate: boolean;
  change: (key: keyof (SubAssembly & { airframeFamily?: string }), value: any) => void;
  close: () => void;
  onSave: (values: Partial<SubAssembly>) => Promise<void>;
};

const inputLabelSx = {
  color: "#fff",
  lineHeight: 1.2,
  overflow: "visible",
  "&.Mui-focused": { color: "#fff" },
  "&.MuiInputLabel-shrink": {
    color: "#fff",
    backgroundColor: "#1a1a1a",
    paddingInline: "6px",
    paddingTop: "2px",
    paddingBottom: "2px",
    transform: "translate(12px, -12px) scale(0.75)",
  },
  "& .MuiInputLabel-asterisk": {
    color: "hsl(19 101% 51% / 1)",
  },
};

const selectSx = {
  color: "#fff",
  backgroundColor: "#1a1a1a",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#fff",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#fff",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(19 101% 51% / 1)",
    borderWidth: 1,
  },
  "& .MuiSelect-icon": {
    color: "#fff",
  },
  "& .MuiOutlinedInput-input": {
    color: "#fff !important",
    backgroundColor: "#1a1a1a",
    padding: "12px 14px",
  },
};

const readOnlyTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    backgroundColor: "#1a1a1a",
    "& fieldset": { borderColor: "#fff" },
    "&:hover fieldset": { borderColor: "#fff" },
    "&.Mui-focused fieldset": {
      borderColor: "hsl(19 101% 51% / 1)",
    },
  },
};

const readOnlySelectSx = {
  ...selectSx,
  "&.Mui-disabled": {
    color: "#fff",
    opacity: 1,
    WebkitTextFillColor: "#fff",
    backgroundColor: "#1a1a1a",
  },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#fff",
  },
  "& .MuiSelect-select.Mui-disabled": {
    color: "#fff",
    opacity: 1,
    WebkitTextFillColor: "#fff",
  },
  "&.Mui-disabled .MuiSelect-icon": {
    color: "#fff",
  },
  "& .MuiSvgIcon-root.Mui-disabled": {
    color: "#fff",
    opacity: 1,
  },
};

const menuProps = {
  PaperProps: {
    sx: {
      backgroundColor: "#222",
      color: "#fff",
      "& .MuiMenuItem-root": {
        color: "#fff",
        "&:hover": {
          backgroundColor: "hsl(19 101% 51% / 0.2)",
        },
        "&.Mui-selected": {
          backgroundColor: "hsl(19 101% 51% / 0.3)",
        },
      },
    },
  },
};

export function SubAssemblyFormDialog({
  open,
  form,
  isCreate,
  change,
  close,
  onSave,
}: Readonly<Props>) {
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [reasonOptions, setReasonOptions] = useState<ShopVisitReasonItem[]>([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const { getReasons } = useShopVisitReasonService();
  const { getSubAssemblyTypes } = useSubAssemblyService();
  const { families, loading: familiesLoading } = useAirframeFamilies();
  const [subAssemblyTypeOptions, setSubAssemblyTypeOptions] = useState<string[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);

  const normalizedLandingGearType = String(form.landingGearType ?? "")
    .trim()
    .toUpperCase();
  const normalizedSubAssemblyType = String(form.subAssemblyType ?? "").trim();

  const supportedFamilies = useMemo(() => {
    const current = normalizeAirframeFamily(String(form.airframeFamily ?? ""));
    return filterSupportedAirframeFamilies([
      ...families,
      ...(current ? [current] : []),
    ]);
  }, [families, form.airframeFamily]);

  const landingGearType = normalizedLandingGearType || "RMLG";
  const airframeFamily = normalizeAirframeFamily(String(form.airframeFamily ?? ""));
  const isAttached = isAttachedSubAssembly(form);
  const isEditMode = !isCreate;
  const isReadOnlyMode = isEditMode && isAttached;
  const isIdentityReadOnlyMode = isEditMode && !isAttached;
  const isTypeAndSerialReadOnly = isReadOnlyMode || isIdentityReadOnlyMode;
  const canEditOffWingDetails = !isAttached;
  const offWingType = normalizeOffWingTypeForForm(form.offWingType);
  const shouldShowReason = offWingType === "In Shop";
  const inshopReasonFromOptions = reasonOptions.find(
    (item) => item.id === form.shopVisitReasonId
  )?.label;
  const inshopReasonDisplayValue =
    String(form.shopVisitReasonLabel ?? "").trim() ||
    String(inshopReasonFromOptions ?? "").trim() ||
    String(form.shopVisitReasonId ?? "").trim();

  const subAssemblyTypeOptionsFallback = useMemo(
    () => getSubAssemblyTypesForLandingGear(landingGearType, airframeFamily),
    [landingGearType, airframeFamily]
  );

  const landingGearTypeOptionsForSelect = useMemo(() => {
    if (!normalizedLandingGearType) {
      return LANDING_GEAR_TYPE_OPTIONS;
    }

    const hasMatch = LANDING_GEAR_TYPE_OPTIONS.some(
      (option) => option.toUpperCase() === normalizedLandingGearType
    );

    return hasMatch
      ? LANDING_GEAR_TYPE_OPTIONS
      : [normalizedLandingGearType, ...LANDING_GEAR_TYPE_OPTIONS];
  }, [normalizedLandingGearType]);

  useEffect(() => {
    if (!open || !airframeFamily || !landingGearType) {
      setSubAssemblyTypeOptions(subAssemblyTypeOptionsFallback);
      return;
    }

    let active = true;
    setTypesLoading(true);
    void getSubAssemblyTypes(landingGearType, airframeFamily)
      .then((types) => {
        if (active) {
          setSubAssemblyTypeOptions(
            types.length > 0 ? types : subAssemblyTypeOptionsFallback
          );
        }
      })
      .catch(() => {
        if (active) setSubAssemblyTypeOptions(subAssemblyTypeOptionsFallback);
      })
      .finally(() => {
        if (active) setTypesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    open,
    airframeFamily,
    landingGearType,
    getSubAssemblyTypes,
    subAssemblyTypeOptionsFallback,
  ]);

  useEffect(() => {
    if (!open || !normalizedSubAssemblyType) return;

    setSubAssemblyTypeOptions((prev) => {
      const hasMatch = prev.some(
        (option) => String(option).trim().toLowerCase() === normalizedSubAssemblyType.toLowerCase()
      );

      if (hasMatch) {
        return prev;
      }

      return [normalizedSubAssemblyType, ...prev];
    });
  }, [open, normalizedSubAssemblyType]);

  useEffect(() => {
    if (!open || !shouldShowReason) {
      setReasonOptions([]);
      setReasonsLoading(false);
      return;
    }

    let active = true;
    setReasonsLoading(true);
    void getReasons("LandingGear")
      .then((reasons) => {
        if (active) {
          setReasonOptions(Array.isArray(reasons) ? reasons : []);
        }
      })
      .catch(() => {
        if (active) {
          setReasonOptions([]);
        }
      })
      .finally(() => {
        if (active) {
          setReasonsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [open, shouldShowReason, getReasons]);

  const parseDate = (dateStr: any): Date | null => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    if (typeof dateStr === "string") {
      const parsed = parse(dateStr, "yyyy-MM-dd", new Date());
      return isValid(parsed) ? parsed : null;
    }
    return null;
  };

  const save = async () => {
    if (isSaving || isReadOnlyMode) return;

    const errors: string[] = [];

    if (!form.landingGearType?.trim()) {
      errors.push("Type of Landing Gear is required.");
    }

    if (!form.subAssemblyType?.trim()) {
      errors.push("Type is required.");
    }

    if (!form.serialNumber?.trim()) {
      errors.push("Serial Number is required.");
    }

    if (!form.partNumber?.trim()) {
      errors.push("Part Number is required.");
    }

    if (canEditOffWingDetails && shouldShowReason && !form.shopVisitReasonId) {
      errors.push("Inshop reason is required when OffWing Type is In Shop.");
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsSaving(true);
    try {
      const normalizedInstallDate = form.installDate?.trim()
        ? form.installDate
        : null;

      await onSave({
        ...form,
        installDate: normalizedInstallDate,
        offWingType: canEditOffWingDetails ? offWingType : form.offWingType,
        shopVisitReasonId:
          canEditOffWingDetails && shouldShowReason
            ? form.shopVisitReasonId
            : form.shopVisitReasonId,
        shopVisitReasonLabel:
          canEditOffWingDetails && shouldShowReason
            ? form.shopVisitReasonLabel
            : form.shopVisitReasonLabel,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: "#111",
          color: "#fff",
          border: "1px solid #333",
        },
      }}
    >
      <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)" }}>
        {isCreate ? "Add Sub-Assembly" : "Edit Sub-Assembly"}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {isEditMode && (
            <Grid size={{ xs: 12 }}>
              <Typography sx={{ color: "#bbb", fontSize: "0.85rem", mb: 0.5 }}>
                {isReadOnlyMode
                  ? "All fields are read-only when this sub-assembly is attached."
                  : "You can edit this sub-assembly because it is not attached."}
              </Typography>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              {isTypeAndSerialReadOnly ? (
                <TextField
                  fullWidth
                  required
                  label="Airframe Type"
                  value={airframeFamily}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ sx: inputLabelSx, required: true }}
                  sx={readOnlyTextFieldSx}
                />
              ) : (
                <>
                  <InputLabel required sx={inputLabelSx}>
                    Airframe Type
                  </InputLabel>
                  <Select
                    value={airframeFamily}
                    label="Airframe Type"
                    onChange={(e) => change("airframeFamily", e.target.value)}
                    sx={readOnlySelectSx}
                    MenuProps={menuProps}
                    disabled={familiesLoading}
                  >
                    {supportedFamilies.map((family) => (
                      <MenuItem key={family} value={family}>
                        {family}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              {isTypeAndSerialReadOnly ? (
                <TextField
                  fullWidth
                  required
                  label="Type of Landing Gear"
                  value={landingGearType}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ sx: inputLabelSx, required: true }}
                  sx={readOnlyTextFieldSx}
                />
              ) : (
                <>
                  <InputLabel required sx={inputLabelSx}>
                    Type of Landing Gear
                  </InputLabel>
                  <Select
                    value={landingGearType}
                    label="Type of Landing Gear"
                    onChange={(e) => change("landingGearType", e.target.value)}
                    sx={readOnlySelectSx}
                    MenuProps={menuProps}
                    disabled={!airframeFamily}
                  >
                    {landingGearTypeOptionsForSelect.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              {isTypeAndSerialReadOnly ? (
                <TextField
                  fullWidth
                  required
                  label="Type"
                  value={normalizedSubAssemblyType}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ sx: inputLabelSx, required: true }}
                  sx={readOnlyTextFieldSx}
                />
              ) : (
                <>
                  <InputLabel required sx={inputLabelSx}>
                    Type
                  </InputLabel>
                  <Select
                    value={normalizedSubAssemblyType}
                    label="Type"
                    onChange={(e) => change("subAssemblyType", e.target.value)}
                    sx={selectSx}
                    MenuProps={menuProps}
                    disabled={!airframeFamily || typesLoading}
                  >
                    {subAssemblyTypeOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              label="Serial Number"
              value={form.serialNumber ?? ""}
              onChange={(e) => change("serialNumber", e.target.value)}
              InputProps={{ readOnly: isTypeAndSerialReadOnly }}
              InputLabelProps={{ sx: inputLabelSx }}
              sx={readOnlyTextFieldSx}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              label="Part Number"
              value={form.partNumber ?? ""}
              onChange={(e) => change("partNumber", e.target.value)}
              InputProps={{ readOnly: isReadOnlyMode }}
              InputLabelProps={{ sx: inputLabelSx, required: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  backgroundColor: "#1a1a1a",
                  "& fieldset": { borderColor: "#fff" },
                  "&:hover fieldset": { borderColor: "#fff" },
                  "&.Mui-focused fieldset": {
                    borderColor: "hsl(19 101% 51% / 1)",
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              label="Install Date"
              value={parseDate(form.installDate)}
              readOnly={isReadOnlyMode}
              onChange={(value) =>
                change("installDate", value ? format(value, "yyyy-MM-dd") : "")
              }
              slotProps={{
                ...darkDatePickerSlotProps,
                textField: {
                  ...darkDatePickerSlotProps.textField,
                  required: false,
                  InputLabelProps: { required: false },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: "hsl(19 101% 51% / 1)",
                fontWeight: "bold",
                mt: 2,
                mb: 1,
              }}
            >
              OffWing Details
            </Typography>
          </Grid>

          {isAttached && (
            <Grid size={{ xs: 12 }}>
              <Typography sx={{ color: "#888", fontSize: "0.85rem" }}>
                Off-wing details can only be edited when this sub-assembly is not
                attached to a landing gear assembly.
              </Typography>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            {isReadOnlyMode ? (
              <TextField
                fullWidth
                label="OffWing Type"
                value={offWingType}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ sx: inputLabelSx }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    backgroundColor: "#1a1a1a",
                    "& fieldset": { borderColor: "#fff" },
                    "&:hover fieldset": { borderColor: "#fff" },
                    "&.Mui-focused fieldset": {
                      borderColor: "hsl(19 101% 51% / 1)",
                    },
                  },
                }}
              />
            ) : (
              <FormControl
                fullWidth
                required={canEditOffWingDetails}
                disabled={!canEditOffWingDetails}
              >
                <InputLabel required={canEditOffWingDetails} sx={inputLabelSx}>
                  OffWing Type
                </InputLabel>
                <Select
                  label="OffWing Type"
                  value={offWingType}
                  onChange={(e) => {
                    const nextOffWingType = e.target.value;
                    change("offWingType", nextOffWingType);
                    if (nextOffWingType !== "In Shop") {
                      change("shopVisitReasonId", "");
                      change("shopVisitReasonLabel", "");
                    }
                  }}
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  {OFF_WING_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>

          {(isReadOnlyMode || shouldShowReason || (isAttached && inshopReasonDisplayValue)) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              {isReadOnlyMode ? (
                <TextField
                  fullWidth
                  label="Inshop Reasons"
                  value={inshopReasonDisplayValue}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ sx: inputLabelSx }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#fff",
                      backgroundColor: "#1a1a1a",
                      "& fieldset": { borderColor: "#fff" },
                      "&:hover fieldset": { borderColor: "#fff" },
                      "&.Mui-focused fieldset": {
                        borderColor: "hsl(19 101% 51% / 1)",
                      },
                    },
                  }}
                />
              ) : (
                <FormControl
                  fullWidth
                  required={canEditOffWingDetails && shouldShowReason}
                  disabled={!canEditOffWingDetails || reasonsLoading}
                >
                  <InputLabel
                    required={canEditOffWingDetails && shouldShowReason}
                    sx={inputLabelSx}
                  >
                    Inshop Reasons
                  </InputLabel>
                  <Select
                    label="Inshop Reasons"
                    value={form.shopVisitReasonId || ""}
                    onChange={(e) => {
                      const selectedId = String(e.target.value || "");
                      const selectedReason = reasonOptions.find(
                        (item) => item.id === selectedId
                      );
                      change("shopVisitReasonId", selectedId);
                      change(
                        "shopVisitReasonLabel",
                        selectedReason?.label || ""
                      );
                    }}
                    sx={selectSx}
                    MenuProps={menuProps}
                  >
                    {reasonOptions.map((opt, index) => (
                      <MenuItem
                        key={`${String(opt.id)}-${opt.label}-${index}`}
                        value={opt.id}
                      >
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={close} sx={{ color: "#fff" }} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={isSaving || isReadOnlyMode}
          sx={{
            backgroundColor: "hsl(19 101% 51% / 1)",
            "&:hover": { backgroundColor: "hsl(19 101% 41% / 1)" },
            "&.Mui-disabled": {
              backgroundColor: "hsl(19 101% 51% / 0.45)",
              color: "rgba(255, 255, 255, 0.75)",
              opacity: 1,
            },
          }}
        >
          {isSaving ? "Saving..." : isCreate ? "Add" : "Save"}
        </Button>
      </DialogActions>

      <ValidationErrorsDialog
        open={validationErrors.length > 0}
        errors={validationErrors}
        onClose={() => setValidationErrors([])}
      />
    </Dialog>
  );
}
